import {
  deriveCoverAlignedCropStateFromImageUrl,
  getCropViewportDimensions,
  type CropViewportState,
} from "./cropImageFile";
import { deriveCropViewportStateFromImages } from "./deriveBannerCropState";
import { parseStoredCropState } from "./cropStatePersist";

export type ResolveCropViewportStateForEditArgs = {
  /** 1) Persisted / in-memory zoom+pan (image_crop JSON). */
  cropState?: CropViewportState | null;
  /** Uncropped source URL shown in the cropper. */
  originalUrl?: string | null;
  /** Current display / cropped URL used for image-diff restore. */
  croppedUrl?: string | null;
  aspectRatio?: number;
  viewport?: { width: number; height: number } | null;
  /**
   * 3) When JSON + derive both unavailable: match CSS cover+center on original.
   * Omit / `contain` → return null (caller/modal keeps contain @ zoom 1).
   */
  defaultFit?: "contain" | "cover";
};

/**
 * Shared restore cascade for ImageCropperModal / InlineImageCropper:
 * 1) persisted cropState
 * 2) derive from original vs cropped URLs
 * 3) optional cover-aligned fallback on the original
 */
export async function resolveCropViewportStateForEdit(
  args: ResolveCropViewportStateForEditArgs
): Promise<CropViewportState | null> {
  const persisted = parseStoredCropState(args.cropState);
  if (persisted) {
    return persisted;
  }

  const originalUrl = String(args.originalUrl ?? "").trim();
  const croppedUrl = String(args.croppedUrl ?? "").trim();

  const resolvedViewport = (() => {
    if (args.viewport?.width && args.viewport?.height) {
      return {
        width: args.viewport.width,
        height: args.viewport.height,
      };
    }
    if (args.aspectRatio == null) return null;
    const dims = getCropViewportDimensions(args.aspectRatio);
    return { width: dims.viewportWidth, height: dims.viewportHeight };
  })();

  if (originalUrl && croppedUrl && originalUrl !== croppedUrl) {
    try {
      if (args.aspectRatio != null) {
        const derived = await deriveCropViewportStateFromImages(
          originalUrl,
          croppedUrl,
          args.aspectRatio,
          resolvedViewport
        );
        if (derived) return derived;
      } else {
        const derived = await deriveCropViewportStateFromImages(
          originalUrl,
          croppedUrl
        );
        if (derived) return derived;
      }
    } catch {
      // fall through to cover / null
    }
  }

  if (args.defaultFit === "cover" && originalUrl && resolvedViewport) {
    try {
      return await deriveCoverAlignedCropStateFromImageUrl(
        originalUrl,
        resolvedViewport.width,
        resolvedViewport.height
      );
    } catch {
      return null;
    }
  }

  return null;
}
